import React, { useState, useEffect, useRef } from 'react'
import { Form, Button } from 'react-bootstrap'
import Datetime from 'react-datetime'
import moment from 'moment'

function DateFilterPopover({ column, params, setParams, onClose, onSetHandleApply, canEdit = true, hideApplyButton = false }) {
  // Handle custom columns differently - they use a different key format
  let minAccessor, maxAccessor, optionAccessor

  if (column.section_abbreviation === 'custom') {
    // For custom columns, extract the ID from accessor_key (format: custom_column_123)
    const customColumnId = column.accessor_key.slice('custom_column_'.length)
    minAccessor = `custom_column_min_${customColumnId}`
    maxAccessor = `custom_column_max_${customColumnId}`
    optionAccessor = `${column.accessor_key}Option`
  } else {
    // Regular column handling
    const prefix = column.section_abbreviation?.toLowerCase()
    // Split accessor key into prefix and suffix parts
    const suffix = column.accessor_key.slice(prefix.length)
    minAccessor = `${prefix}Min${suffix}`
    maxAccessor = `${prefix}Max${suffix}`
    optionAccessor = `${column.accessor_key}Option`
  }

  const [operator, setOperator] = useState(params[optionAccessor] || 'any')
  const dateFormat = 'MM/DD/YYYY'
  const [dateError, setDateError] = useState('')

  const options = [
    {
      label: 'Comparison',
      options: [
        { value: 'any', label: 'Any Date' },
        { value: 'on', label: 'On (Exact Date)' },
        { value: 'not_on', label: 'Not On (Exact Date)' },
        { value: 'less', label: 'Before' },
        { value: 'greater', label: 'After' },
        { value: 'between', label: 'Between' },
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' }
      ]
    },
    {
      label: 'Relative',
      options: [
        { value: 'Yesterday', label: 'Yesterday' },
        { value: 'Past 7 days', label: 'Past 7 days' },
        { value: 'This Week', label: 'This Week' },
        { value: 'Last Week', label: 'Last Week' },
        { value: '30', label: 'Past 30 days' },
        { value: '60', label: 'Past 60 days' },
        { value: '90', label: 'Past 90 days' },
        { value: '120', label: 'Past 120 days' },
        { value: 'This Month', label: 'This Month' },
        { value: 'Last Month', label: 'Last Month' },
        { value: '1 year', label: 'More than 1 year ago' },
        { value: '2 years', label: 'More than 2 years ago' },
        { value: '3 years', label: 'More than 3 years ago' }
      ]
    },
    {
      label: 'Quarters',
      options: [
        { value: 'CQ', label: 'Current Quarter' },
        { value: 'LQ', label: 'Last Quarter' },
        { value: 'Q1', label: 'Q1' },
        { value: 'Q2', label: 'Q2' },
        { value: 'Q3', label: 'Q3' },
        { value: 'Q4', label: 'Q4' }
      ]
    },
    {
      label: 'Calendar Year',
      options: [
        { value: 'CY', label: 'Current Calendar Year' },
        { value: 'LY', label: 'Last Calendar Year' }
      ]
    },
    {
      label: 'Fiscal Year',
      options: [
        { value: 'PFY', label: 'Previous Fiscal Year' },
        { value: 'FY', label: 'Current Fiscal Year' }
      ]
    }
  ]

  const [minDate, setMinDate] = useState(
    params[minAccessor] ? moment(params[minAccessor]).format(dateFormat) : null
  )
  const [maxDate, setMaxDate] = useState(
    params[maxAccessor] ? moment(params[maxAccessor]).format(dateFormat) : null
  )

  // Track initial values to detect changes
  const initialOperator = useRef(params[optionAccessor] || 'any')
  const initialMinDate = useRef(params[minAccessor] ? moment(params[minAccessor]).format(dateFormat) : null)
  const initialMaxDate = useRef(params[maxAccessor] ? moment(params[maxAccessor]).format(dateFormat) : null)

  // Clear dates based on operator type and logic
  useEffect(() => {
    const comparisonOptions = ['any', 'on', 'not_on', 'less', 'greater', 'between', 'is_empty', 'is_not_empty']

    if (!comparisonOptions.includes(operator) || operator === 'any') {
      setMinDate(null)
      setMaxDate(null)
    } else if (operator === 'on' || operator === 'not_on') {
      // For 'on'/'not_on', keep minDate only (maxDate will be set to same value on apply)
      setMaxDate(null)
    } else if (operator === 'less') {
      setMinDate(null)
    } else if (operator === 'greater') {
      setMaxDate(null)
    } else if (operator === 'is_empty' || operator === 'is_not_empty') {
      setMinDate(null)
      setMaxDate(null)
    }
  }, [operator])

  const validateDate = (dateValue) => {
    if (!dateValue) return true

    // Handle moment objects (passed by react-datetime for valid dates)
    if (moment.isMoment(dateValue)) {
      return dateValue.isValid()
    }

    // Handle strings (passed by react-datetime for invalid dates)
    const date = moment(dateValue, dateFormat, true)
    return date.isValid()
  }

  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return true
    const start = moment(startDate, dateFormat)
    const end = moment(endDate, dateFormat)
    return start.isSameOrBefore(end)
  }

  const handleDateChange = (value, isMinDate = false) => {
    if (!canEdit) return
    setDateError('')

    // Convert moment object to string for consistent state management
    const stringValue = moment.isMoment(value) ? value.format(dateFormat) : value

    if (isMinDate) {
      setMinDate(stringValue)
      if (operator === 'greater') {
        setMaxDate(null)
      }

      if (value && !validateDate(value)) {
        setDateError('Invalid start date format')
        return
      }
      if (operator === 'between' && value && maxDate && !validateDateRange(stringValue, maxDate)) {
        setDateError('Start date must be before end date')
        return
      }
    } else {
      setMaxDate(stringValue)
      if (operator === 'less') {
        setMinDate(null)
      }

      if (value && !validateDate(value)) {
        setDateError('Invalid end date format')
        return
      }
      if (operator === 'between' && minDate && value && !validateDateRange(minDate, stringValue)) {
        setDateError('End date must be after start date')
        return
      }
    }
  }

  const updateParams = () => {
    // Determine which option groups this operator belongs to
    const comparisonOptions = ['any', 'on', 'not_on', 'less', 'greater', 'between', 'is_empty', 'is_not_empty']
    const relativeOptions = ['Yesterday', 'Past 7 days', 'This Week', 'Last Week', '30', '60', '90', '120', 'This Month', 'Last Month', '1 year', '2 years', '3 years', 'CY', 'LY']
    const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4', 'CQ', 'LQ']
    const fiscalYearOptions = ['PFY', 'FY']

    let finalMinDate, finalMaxDate

    // Only send min/max dates for comparison options
    // For relative, quarters, and fiscal year options, only send the option value
    if (operator === 'is_empty' || operator === 'is_not_empty') {
      // No dates needed for empty/not empty checks
      finalMinDate = null
      finalMaxDate = null
    } else if (operator === 'on' || operator === 'not_on') {
      // For 'on'/'not_on', set both min and max to the same date; the backend
      // negates via the option key for 'not_on'.
      const onDate = minDate ? moment(minDate, dateFormat).toDate() : null
      finalMinDate = onDate
      finalMaxDate = onDate
    } else if (comparisonOptions.includes(operator)) {
      // For comparison options, use manually entered dates
      finalMinDate = minDate ? moment(minDate, dateFormat).toDate() : null
      finalMaxDate = maxDate ? moment(maxDate, dateFormat).toDate() : null
    } else if (relativeOptions.includes(operator) || quarterOptions.includes(operator) || fiscalYearOptions.includes(operator)) {
      // For relative, quarters, and fiscal year options, don't send dates
      finalMinDate = null
      finalMaxDate = null
    }

    // Get the current params to ensure we have the latest sort state
    setParams(currentParams => {
      const newParams = { ...currentParams }

      // Only set date fields if they have values, otherwise remove them
      if (finalMinDate) {
        newParams[minAccessor] = finalMinDate
      } else {
        delete newParams[minAccessor]
      }

      if (finalMaxDate) {
        newParams[maxAccessor] = finalMaxDate
      } else {
        delete newParams[maxAccessor]
      }

      if (operator !== 'any') {
        newParams[optionAccessor] = operator
      } else {
        delete newParams[optionAccessor]
      }

      return newParams
    })
  }

  const handleApply = () => {
    if (dateError) {
      return
    }

    if ((operator === 'on' || operator === 'not_on') && minDate && !validateDate(minDate)) {
      setDateError('Invalid date format')
      return
    }

    if (operator === 'greater' && minDate && !validateDate(minDate)) {
      setDateError('Invalid start date format')
      return
    }

    if (operator === 'less' && maxDate && !validateDate(maxDate)) {
      setDateError('Invalid end date format')
      return
    }

    if (operator === 'between') {
      if (minDate && !validateDate(minDate)) {
        setDateError('Invalid start date format')
        return
      }
      if (maxDate && !validateDate(maxDate)) {
        setDateError('Invalid end date format')
        return
      }
      if (minDate && maxDate && !validateDateRange(minDate, maxDate)) {
        setDateError('Start date must be before end date')
        return
      }
    }

    // Check if any values have changed
    const hasChanges = operator !== initialOperator.current ||
                       minDate !== initialMinDate.current ||
                       maxDate !== initialMaxDate.current

    if (!hasChanges) {
      onClose()
      return
    }

    updateParams()
    onClose()
  }

  useEffect(() => {
    if (onSetHandleApply) {
      onSetHandleApply(handleApply)
    }
  }, [operator, minDate, maxDate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply changes when hideApplyButton is true
  useEffect(() => {
    if (hideApplyButton && !dateError) {
      updateParams()
    }
  }, [operator, minDate, maxDate, hideApplyButton, dateError]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => {
    setMinDate(null)
    setMaxDate(null)
    setOperator('any')
    setDateError('')
    const newParams = {
      ...params,
      [minAccessor]: null,
      [maxAccessor]: null
    }
    delete newParams[optionAccessor]
    setParams(newParams)
  }

  return (
    <div>
      <div className="mb-3">
        <strong>{column.name}</strong>
      </div>

      <div className="mb-3">
        <Form.Select
          value={operator}
          onChange={(e) => canEdit && setOperator(e.target.value)}
          size="sm"
          disabled={!canEdit}
        >
          {options.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Form.Select>
      </div>

      {(operator === 'on' || operator === 'not_on') && (
        <div className="mb-3">
          <Form.Label className="small">Date</Form.Label>
          <Datetime
            dateFormat={dateFormat}
            input={true}
            placeholderText="Select Date"
            timeFormat={false}
            onChange={(value) => handleDateChange(value, true)}
            value={minDate || null}
            inputProps={{ className: 'form-control form-control-sm', disabled: !canEdit }}
          />
        </div>
      )}

      {operator === 'greater' && (
        <div className="mb-3">
          <Form.Label className="small">Start Date</Form.Label>
          <Datetime
            dateFormat={dateFormat}
            input={true}
            placeholderText="Start Date"
            timeFormat={false}
            onChange={(value) => handleDateChange(value, true)}
            value={minDate || null}
            inputProps={{ className: 'form-control form-control-sm', disabled: !canEdit }}
          />
        </div>
      )}

      {operator === 'less' && (
        <div className="mb-3">
          <Form.Label className="small">End Date</Form.Label>
          <Datetime
            dateFormat={dateFormat}
            input={true}
            placeholderText="End Date"
            timeFormat={false}
            onChange={(value) => handleDateChange(value)}
            value={maxDate || null}
            inputProps={{ className: 'form-control form-control-sm', disabled: !canEdit }}
          />
        </div>
      )}

      {operator === 'between' && (
        <>
          <div className="mb-3">
            <Form.Label className="small">Start Date</Form.Label>
            <Datetime
              dateFormat={dateFormat}
              input={true}
              placeholder="Start Date"
              timeFormat={false}
              onChange={(value) => handleDateChange(value, true)}
              value={minDate || null}
              inputProps={{ className: 'form-control form-control-sm', disabled: !canEdit }}
            />
          </div>
          <div className="mb-3">
            <Form.Label className="small">End Date</Form.Label>
            <Datetime
              dateFormat={dateFormat}
              input={true}
              placeholderText="End Date"
              timeFormat={false}
              onChange={(value) => handleDateChange(value)}
              value={maxDate || null}
              inputProps={{ className: 'form-control form-control-sm', disabled: !canEdit }}
            />
          </div>
        </>
      )}

      {dateError && (
        <div className="mb-3">
          <div className="text-danger small">{dateError}</div>
        </div>
      )}

      {!hideApplyButton && (
        <div className="d-flex justify-content-between gap-2">
          {canEdit && (
            <Button variant="light" size="sm" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={canEdit ? handleApply : onClose}
            disabled={canEdit && !!dateError}
          >
            {canEdit ? 'Apply' : 'Close'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default DateFilterPopover
